/**
 * Pack-type switcher (stick packs <-> tub) for the PDP buy box.
 *
 * The two pack types are separate products, so switching is a product change.
 * Instead of a full navigation this replays the combined-listing path from
 * variant-picker.js: announce a ProductSelectEvent, fetch the other product's
 * page, morph <main> in place, then resolve the event with the new variant so
 * the gallery, price, product form and sticky bar update themselves. The URL,
 * title and canonical follow, and the back button reverses the switch.
 *
 * Inside a landing page's featured-product section only that section is
 * re-rendered, from the sibling product's URL with the section's id (the
 * section shows the routed product when there is one), and the page URL is
 * left alone.
 *
 * The sibling page is fetched while the browser is idle (and again on hover
 * or focus) so the click itself has nothing left to wait for. Without JS the
 * segments are plain links and the switch is an ordinary navigation.
 */
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { requestIdleCallback } from '@theme/utilities';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

const CACHE_LIMIT = 12;

/** @type {Map<string, Promise<string>>} */
const pageCache = new Map();

/**
 * Fetches a page's HTML, sharing in-flight and completed requests by URL.
 * @param {string} href
 * @returns {Promise<string>}
 */
function fetchPage(href) {
  const cached = pageCache.get(href);
  if (cached) return cached;

  const request = fetch(href, { headers: { Accept: 'text/html' } })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch pack type: HTTP error ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      pageCache.delete(href);
      throw error;
    });

  if (pageCache.size >= CACHE_LIMIT) {
    const oldest = pageCache.keys().next().value;
    if (oldest) pageCache.delete(oldest);
  }
  pageCache.set(href, request);

  return request;
}

/**
 * @typedef {object} VariantPickerLike
 * @property {DOMStringMap} dataset
 * @property {() => unknown[]} [getAllSelectedOptions]
 * @property {string | undefined} [selectedOptionId]
 */

class PdpPackTypeComponent extends Component {
  #abortController = new AbortController();

  /** @type {AbortController | undefined} */
  #switchController;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    window.addEventListener('popstate', this.#handlePopState, { signal });
    // A flavor change rewrites the sibling links (same flavor, other pack
    // type), so warm the new targets once that response has landed.
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#schedulePrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#switchController?.abort();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  /** The featured-product section this toggle lives in, if not on the PDP */
  get #featured() {
    return this.closest('featured-product-information');
  }

  /**
   * The URL to fetch for a sibling link: the page itself on the PDP, just
   * this section's render of it in a featured section.
   * @param {string} href
   */
  #requestUrl(href) {
    const featured = this.#featured;
    if (!featured?.id) return href;

    const url = new URL(href, location.origin);
    url.searchParams.set('section_id', featured.id);
    return url.href;
  }

  /** @returns {HTMLAnchorElement[]} */
  get #links() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('a[href]')]);
  }

  /** @returns {VariantPickerLike | null} */
  get #variantPicker() {
    return /** @type {VariantPickerLike | null} */ (this.#section?.querySelector('variant-picker') ?? null);
  }

  /**
   * Warms the cache for the other pack type. Skipped when the shopper asked
   * for reduced data use.
   * @param {Event} [event] - A pointerenter / focus on one segment
   */
  prefetch(event) {
    if (/** @type {{ saveData?: boolean } | undefined} */ (navigator.connection)?.saveData) return;

    const target = event?.target instanceof Element ? event.target.closest('a[href]') : null;
    const links = target instanceof HTMLAnchorElement ? [target] : this.#links;

    for (const link of links) {
      fetchPage(this.#requestUrl(link.href)).catch(() => {});
    }
  }

  #schedulePrefetch() {
    requestIdleCallback(() => {
      if (this.isConnected) this.prefetch();
    });
  }

  /**
   * Switches pack type in place. Modified clicks (new tab, etc.) and the
   * theme editor keep the plain link behaviour.
   * @param {MouseEvent} event
   */
  select(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.Shopify?.designMode) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    this.#switchTo(link.href, { push: true });
  }

  #handlePopState = () => {
    // Variant changes use replaceState, so a popstate that lands on another
    // product path is a pack-type switch being undone or redone. Featured
    // sections never touch the URL.
    if (this.#featured) return;
    const currentPath = this.dataset.productUrl?.split('?')[0];
    if (!currentPath || location.pathname === currentPath) return;
    if (!this.#links.some((link) => new URL(link.href).pathname === location.pathname)) return;

    this.#switchTo(location.href, { push: false });
  };

  /** @param {Event} event */
  #handleProductSelect = (event) => {
    if (event.target === this) return;
    const promise = /** @type {Event & { promise?: Promise<unknown> }} */ (event).promise;
    promise?.then(() => this.#schedulePrefetch()).catch(() => {});
  };

  /**
   * @param {string} href
   * @param {{ push: boolean }} options
   */
  async #switchTo(href, { push }) {
    const url = new URL(href, location.origin);
    const section = this.#section;
    const variantPicker = this.#variantPicker;

    this.#switchController?.abort();
    const controller = new AbortController();
    this.#switchController = controller;

    const deferred = ProductSelectEvent.createPromise();

    this.setAttribute('aria-busy', 'true');
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: variantPicker?.dataset.productId ?? '',
          title: variantPicker?.dataset.productTitle ?? '',
          handle: variantPicker?.dataset.productHandle ?? '',
        },
        selectedOptions: variantPicker?.getAllSelectedOptions?.() ?? [],
        detail: {
          optionValueId: '',
          variantId: url.searchParams.get('variant') ?? '',
          connectedProductUrl: url.pathname,
        },
        promise: deferred.promise,
      })
    );

    try {
      const responseText = await fetchPage(this.#requestUrl(url.href));
      controller.signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const featured = this.#featured;
      const target = featured ?? document.querySelector('main');
      const newTarget = featured ? html.querySelector('featured-product-information') : html.querySelector('main');
      if (!target || !newTarget) throw new Error('No product content in the pack-type response');

      // Morph from a clone: listeners on the ProductSelectEvent read `html`
      // afterwards (the media gallery swaps itself for the response's gallery).
      morph(target, newTarget.cloneNode(true));

      // The pack-size radios opt out of morphing so a flavor change keeps the
      // tier; a pack-type change starts from the new product's own default.
      const newDefaultTier = html.querySelector('pdp-pack-size-component input[type="radio"][checked]');
      if (newDefaultTier instanceof HTMLInputElement) {
        const liveTier = section?.querySelector(
          `pdp-pack-size-component input[type="radio"][value="${newDefaultTier.value}"]`
        );
        if (liveTier instanceof HTMLInputElement) liveTier.checked = true;
      }

      if (!featured) {
        document.title = html.title;
        const canonical = document.querySelector('link[rel="canonical"]');
        const newCanonical = html.querySelector('link[rel="canonical"]');
        if (canonical && newCanonical) canonical.setAttribute('href', newCanonical.getAttribute('href') ?? '');

        if (push && url.href !== location.href) {
          history.pushState({ pdpPackType: true }, '', url.href);
        }
      }

      const variantPickerJsonScript = html.querySelector('variant-picker script[type="application/json"]');
      const textContent = variantPickerJsonScript?.textContent;
      const resource = textContent ? JSON.parse(textContent) : null;
      const productViewAttr = variantPickerJsonScript
        ?.closest('[view-event-payload]')
        ?.getAttribute('view-event-payload')
        ?.trim();

      // The morph has already moved the page's components onto the new
      // product, so resolve with its id: listeners compare it to their own.
      const morphedPicker = this.#variantPicker;

      deferred.resolve({
        variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
        detail: {
          html,
          productId: morphedPicker?.dataset.productId ?? '',
          sourceId: morphedPicker?.selectedOptionId,
          resource: resource && typeof resource === 'object' ? resource : null,
        },
      });

      // The product view fires once per element on intersection; the element
      // survived the morph with a new payload, so announce the new product.
      const productComponent = /** @type {{ dispatchViewEvent?: () => void } | null} */ (
        section?.querySelector('product-component')
      );
      productComponent?.dispatchViewEvent?.();

      this.#schedulePrefetch();
    } catch (error) {
      deferred.reject(error);
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error(error);
      // Fall back to the link's ordinary behaviour
      location.assign(url.href);
    } finally {
      if (this.#switchController === controller) {
        this.#switchController = undefined;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('pdp-pack-type-component')) {
  customElements.define('pdp-pack-type-component', PdpPackTypeComponent);
}
